import { projectDisplayName } from '../organization/memberDirectory';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import {
  BROWSER_TRACKER_UI_CAPABILITIES,
  getRecordTitle,
  TrackerNavigation,
  TrackersUIProvider,
  useTrackerDataSelector,
  type BrowserTrackerSchemaState,
  type SavedView,
  type TrackerAccessTerminationReason,
  type TrackerIdentity,
  type TrackerPresenceMember,
  type TrackerSyncStatus,
} from '@nimbalyst/collab-bundle/trackers-ui';
import '@nimbalyst/collab-bundle/styles.css';
import {
  EditorBreadcrumb,
  EditorHeaderBar,
} from '@nimbalyst/collab-bundle/docs-ui';
import type { TeamSummary } from '../api';
import type { TeamSession } from '../auth/teamJwt';
import { ProjectSwitcher } from '../collab/CollabDocsShell';
import { pruneTeamDocsScopes } from '../collab/docsScopeTeardown';
import { CollabLoadingPanel } from '../components/Chrome';
import { useElementWidth, usePanelResize } from '../components/PanelResizer';
import { DecorativeIcon } from '../organization/DecorativeIcon';
import { isPhoneViewport } from '../phoneViewport';
import { TrackerBrowseStateProvider } from './TrackerBrowseState';
import { useTrackerPhoneDrawer } from './useTrackerPhoneDrawer';
import { getProjectRouteId } from '../projectSelection';
import { DocsSurfaceError, type DocsSurfaceErrorKind } from '../routes/DocsRoutes';
import { orgTrackerItemPath, orgTrackerSetupPath, orgTrackersPath, orgTrackerTypePath } from '../routing';
import { createRecentActivitySavedView, resolveTrackerViewSelection } from './trackerViewState';
import type { BrowserTrackerSession } from './useBrowserTrackerSession';
import { registerQuickOpenProvider } from '../quickOpen/quickOpenSources';

type TeamProject = TeamSummary['projects'][number];

const TRACKERS_PANE_ID = 'browser-trackers-pane';
const TRACKERS_PANE_DEFAULT_WIDTH = 268;
const TRACKERS_PANE_MIN_WIDTH = 200;
/** What the pane must leave for the view beside it; the view is the point. */
const TRACKERS_PANE_MIN_CONTENT_WIDTH = 520;
const TRACKERS_DETAIL_ID = 'browser-trackers-detail-pane';
const TRACKERS_DETAIL_DEFAULT_WIDTH = 560;
const TRACKERS_DETAIL_MIN_WIDTH = 380;
/**
 * What the detail must leave for the list beside it. An item opens *next to*
 * the rows rather than over them, so a list squeezed to nothing would defeat
 * the layout; below this the container query hands the whole region to the
 * detail instead of shaving the list further.
 */
const TRACKERS_DETAIL_MIN_LIST_WIDTH = 320;
const PRESENCE_FACES = 4;
/** The ring is the page background so overlapping faces cut into each other. */
const PRESENCE_FACE = 'grid size-[22px] -ml-1.5 place-items-center rounded-full border-2 border-[color:var(--nim-bg)] bg-nim-primary text-[0.6rem] font-semibold text-nim-on-primary first:ml-0';

export interface TrackersShellContextValue {
  team: TeamSummary;
  project: TeamProject;
  projectId: string;
  schema: BrowserTrackerSchemaState;
  identity: TrackerIdentity | null;
  navigate: (path: string) => void;
  /** Route helper bound to this project, so surfaces do not restate the shape. */
  itemPath: (itemId: string) => string;
  /** Raw URL state. Resolved against the room's views by `useTrackerSelection`. */
  trackerType: string | null;
  viewId: string | null;
  activeItemId: string | null;
}

/**
 * Exported for the mount-invariant test, which has to stand a real
 * `TrackerViewSurface` inside a real `TrackersFrame` to mean anything. Surfaces
 * read it through `useTrackersShell`.
 */
export const TrackersShellContext = createContext<TrackersShellContextValue | null>(null);

export function useTrackersShell(): TrackersShellContextValue {
  const value = useContext(TrackersShellContext);
  if (!value) throw new Error('Tracker surfaces must render inside TrackersShell');
  return value;
}

/**
 * What the URL means, once the room's saved views are known.
 *
 * Resolved here rather than at the route boundary because a view id can only be
 * matched against views the room has sent, and those arrive after the route
 * does. Every consumer -- pane, breadcrumb, surface -- reads the same answer.
 */
export function useTrackerSelection() {
  const { trackerType, viewId } = useTrackersShell();
  const savedViews = useTrackerDataSelector(state => state.savedViews);
  const views = useMemo<SavedView[]>(
    () => [createRecentActivitySavedView(), ...savedViews],
    [savedViews],
  );
  return useMemo(
    () => ({ views, ...resolveTrackerViewSelection({ trackerType, viewId, savedViews: views }) }),
    [views, trackerType, viewId],
  );
}

const SYNC_LABEL: Record<TrackerSyncStatus, string> = {
  disconnected: 'Offline',
  connecting: 'Connecting',
  syncing: 'Syncing',
  connected: 'Live',
  error: 'Sync error',
};

/** The chip borrows its border from `currentColor`, so one class covers both. */
const SYNC_TONE: Record<TrackerSyncStatus, string> = {
  disconnected: 'text-nim-faint',
  connecting: 'text-nim-faint',
  syncing: 'text-nim-faint',
  connected: 'text-nim-success',
  error: 'text-nim-error',
};

/**
 * What the reader is told when the room has refused this tab for good.
 *
 * Written per reason rather than reusing the server's close-frame text, which
 * is four words long and addressed to an operator. The panel itself is the
 * console's existing one -- the same block a docs route renders for the same
 * three situations -- because a second permission panel that says almost the
 * same thing is how the two surfaces start disagreeing.
 */
const ACCESS_COPY: Record<
  TrackerAccessTerminationReason,
  { kind: DocsSurfaceErrorKind; detail: string }
> = {
  'signed-out': {
    kind: 'session-expired',
    detail: 'Your browser session ended, so this tab is no longer connected to the tracker room. Sign in again to reopen it.',
  },
  'not-a-member': {
    kind: 'no-access',
    detail: 'This session does not name a member of this organization, so its trackers cannot be opened here.',
  },
  'removed-from-org': {
    kind: 'removed-member',
    detail: 'Your membership in this team ended. This tab has stopped showing the team’s tracker items, and reconnecting will not restore them.',
  },
  'tracker-access-revoked': {
    kind: 'no-access',
    detail: 'Your access to this project’s trackers was removed. This tab has stopped showing its items, and reconnecting will not restore them.',
  },
};

/**
 * Replaces the whole tracker frame once access is gone -- pane, breadcrumb and
 * view together, not a banner over them.
 *
 * Everything behind this gate is drawn from a room this tab may no longer read.
 * Leaving the rows up under a warning would keep a revoked member reading team
 * data, and leaving the pane up would offer navigation into more of it.
 *
 * There is a bounded lag before this fires. `TeamTrackerRoom` re-checks
 * authorization on every frame it handles and on the 30-second presence
 * heartbeat, so a revocation is noticed within roughly half a minute at worst
 * -- sooner if the reader touches anything or the room broadcasts. None of the
 * copy above claims the removal was instant, because it was not.
 */
export function TrackersAccessGate({ team, children }: { team?: TeamSummary; children: ReactNode }) {
  const sync = useTrackerDataSelector(state => state.sync);
  const access = sync.access ?? null;
  const removedFromOrg = access?.reason === 'removed-from-org';

  /*
   * A live room is the fastest terminal-removal signal the console gets -- the
   * team JWT in hand may not expire for minutes yet, so nothing else has
   * noticed. Shared Docs titles are held in scope-keyed atoms that outlive
   * their session, and ⌘K reads them from any mode, so this is the moment to
   * drop them. Only `removed-from-org`: `tracker-access-revoked` is one
   * project's tracker room, and says nothing about documents.
   */
  useEffect(() => {
    if (team && removedFromOrg) pruneTeamDocsScopes(team);
  }, [removedFromOrg, team]);

  if (!access) return <>{children}</>;
  const copy = ACCESS_COPY[access.reason];
  return <DocsSurfaceError kind={copy.kind} detail={copy.detail} />;
}

/**
 * Publishes this room's items to ⌘K for as long as the room is *readable*.
 *
 * Inside the access gate, not beside it: the registration hands whole item
 * titles to the palette, so a revoked member with the gate up would still have
 * read them back through ⌘K. Unmounting here unregisters, which is also what
 * tells an already-open palette to drop the rows it copied.
 *
 * A component rather than a hook call in `TrackersShell`, because the records
 * only exist below `TrackersUIProvider`. The registration itself is stable and
 * reads through a ref: re-registering on every room frame would churn a Map on
 * a socket's cadence, and the palette reads the corpus once when it opens
 * anyway.
 */
function TrackerQuickOpenRegistration() {
  const { itemPath } = useTrackersShell();
  const records = useTrackerDataSelector(state => state.records);
  const latest = useRef<{ records: typeof records; itemPath: typeof itemPath }>({ records, itemPath });
  latest.current = { records, itemPath };

  useEffect(() => registerQuickOpenProvider('trackers', () => (
    latest.current.records.map((record) => ({
      kind: 'tracker' as const,
      id: record.id,
      title: getRecordTitle(record).trim() || record.issueKey || record.id,
      subtitle: record.primaryType,
      key: record.issueKey ?? null,
      updatedAt: record.system.updatedAt ? Date.parse(record.system.updatedAt) : null,
      icon: 'checklist',
      path: latest.current.itemPath(record.id),
    }))
  )), []);

  return null;
}

function PresenceFaces({ members }: { members: readonly TrackerPresenceMember[] }) {
  if (members.length === 0) return null;
  const shown = members.slice(0, PRESENCE_FACES);
  return (
    <div className="trackers-presence flex items-center" aria-label={`${members.length} teammates in this tracker`}>
      {shown.map((member) => (
        <span className={`trackers-presence-face ${PRESENCE_FACE}`} key={member.teamMemberId} title={member.displayName}>
          {member.displayName.slice(0, 1).toUpperCase()}
        </span>
      ))}
      {members.length > shown.length && (
        <span className={`trackers-presence-face ${PRESENCE_FACE}`}>{`+${members.length - shown.length}`}</span>
      )}
    </div>
  );
}

/**
 * The pane: the project it belongs to, then saved views, then the type tree.
 *
 * `TrackerNavigation` is the shared component desktop's sidebar is built from,
 * so the two cannot drift apart. Everything that *organizes* the tree -- create
 * a folder, move a tracker into one, rename, share or delete a view -- is
 * deliberately absent. This surface navigates the tree; the desktop arranges it.
 */
function TrackersPane({
  onSelectProject,
}: {
  onSelectProject: (project: TeamProject) => void;
}) {
  const { team, project, projectId, schema, navigate } = useTrackersShell();
  const records = useTrackerDataSelector(state => state.records);
  const loaded = useTrackerDataSelector(state => state.loaded);
  const { views, selectedType, activeSavedViewId } = useTrackerSelection();
  // Tracked as what the reader has *closed*, not what they have opened. Folders
  // start open: a tab that has never been used has no stored tree state, and a
  // collapsed tree on first paint reads as "this project has no trackers."
  const [collapsedFolderIds, setCollapsedFolderIds] = useState<ReadonlySet<string>>(new Set());

  const expanded = useMemo(() => schema.navigationEntries
    .filter((entry) => entry.kind === 'folder' && !collapsedFolderIds.has(entry.folderId))
    .map((entry) => (entry as { folderId: string }).folderId),
  [schema.navigationEntries, collapsedFolderIds]);

  const toggleFolder = useCallback((folderId: string) => {
    setCollapsedFolderIds((previous) => {
      const next = new Set(previous);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }, []);

  const countsByType = useMemo(() => {
    const counts = new Map<string, number>();
    for (const record of records) {
      for (const tag of record.typeTags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
    return counts;
  }, [records]);

  return (
    <>
      <div className="browser-trackers-pane-header flex shrink-0 flex-col gap-px border-b border-nim py-2 pl-[0.7rem] pr-2">
        <ProjectSwitcher
          team={team}
          project={project}
          projects={team.projects}
          onSelectProject={onSelectProject}
        />
        <span className="browser-trackers-pane-sub truncate text-[0.69rem] text-nim-muted opacity-80">
          {loaded
            ? `${team.name} · ${records.length === 1 ? '1 item' : `${records.length} items`}`
            : team.name}
        </span>
        <button
          type="button"
          className="browser-trackers-setup-link mt-1 flex w-fit cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-[0.72rem] text-nim-muted hover:text-nim hover:underline"
          onClick={() => navigate(orgTrackerSetupPath(team, projectId))}
        >
          <DecorativeIcon icon="schema" size={13} />
          Tracker setup
        </button>
      </div>
      <div className="browser-trackers-pane-body min-h-0 flex-1 overflow-hidden [&>*]:h-full [&>*]:min-h-0">
        <TrackerNavigation
          trackerTypes={schema.trackerTypes}
          navigationEntries={schema.navigationEntries}
          savedViews={views}
          activeSavedViewId={activeSavedViewId}
          selectedType={selectedType}
          countsByType={countsByType}
          expandedFolderIds={expanded}
          onToggleFolder={toggleFolder}
          onSelectType={(type) => navigate(
            type === 'all'
              ? orgTrackersPath(team, projectId)
              : orgTrackerTypePath(team, projectId, type),
          )}
          onApplyView={(view) => navigate(orgTrackersPath(team, projectId, view.id))}
          // No delete or reshare handler: those are mutations this surface does
          // not perform, so the controls are absent rather than inert.
        />
      </div>
    </>
  );
}

/**
 * The breadcrumb strip, mirroring the desktop's `UnifiedEditorHeaderBar` the
 * same way the docs surface's does -- team, project, then whatever is focused.
 * Connectivity and presence sit on this row rather than in a second strip.
 */
function TrackersMobileContext() {
  const { schema, project } = useTrackersShell();
  const { views, selectedType, activeSavedViewId } = useTrackerSelection();
  const model = schema.trackerTypes.find(type => type.type === selectedType);
  const label = views.find(view => view.id === activeSavedViewId)?.name || model?.displayNamePlural || model?.displayName || 'All trackers';
  const sync = useTrackerDataSelector(state => state.sync);
  return <><strong>{label}</strong><span>{project.name} · {SYNC_LABEL[sync.status]}</span></>;
}

function TrackersBreadcrumb() {
  const { team, project, projectId, schema, navigate, activeItemId } = useTrackersShell();
  const recordsById = useTrackerDataSelector(state => state.recordsById);
  const presence = useTrackerDataSelector(state => state.presence);
  const sync = useTrackerDataSelector(state => state.sync);
  const { views, selectedType, activeSavedViewId } = useTrackerSelection();
  const item = activeItemId ? recordsById.get(activeItemId) ?? null : null;
  const view = activeSavedViewId
    ? views.find((candidate) => candidate.id === activeSavedViewId)
    : null;
  const typeModel = schema.trackerTypes.find((model) => model.type === selectedType);
  const typeLabel = selectedType === 'all'
    ? 'All trackers'
    : typeModel?.displayNamePlural || typeModel?.displayName || selectedType;

  const projectLabel = projectDisplayName(project);
  // A team whose project carries the team's own name is the common shape for a
  // single-project org, and it rendered as `nimbalyst / nimbalyst / All
  // trackers`. The team segment is the redundant one: the pane header beside it
  // already states the team and its item count.
  const showTeamSegment = team.name.trim().toLowerCase() !== projectLabel.trim().toLowerCase();

  const toTrackersHome = () => navigate(orgTrackersPath(team, projectId));

  return (
    <EditorHeaderBar
      breadcrumb={(
        <EditorBreadcrumb
          crumbs={[
            ...(showTeamSegment ? [{ id: 'team', label: team.name }] : []),
            { id: 'project', label: projectLabel, onClick: toTrackersHome },
            ...(item
              ? [
                { id: 'trackers', label: 'Trackers', onClick: toTrackersHome },
                {
                  id: 'item',
                  label: String(item.fields.title ?? item.issueKey ?? item.id),
                  current: true,
                },
              ]
              : [{ id: 'view', label: view ? view.name : typeLabel, current: true }]),
          ]}
        />
      )}
      actions={(
        <>
          <PresenceFaces members={presence} />
          <span
            className={`trackers-sync-chip inline-flex items-center rounded-full border border-current px-2 py-0.5 text-[0.68rem] font-medium ${SYNC_TONE[sync.status]}`}
            data-sync-status={sync.status}
          >
            {SYNC_LABEL[sync.status]}
          </span>
        </>
      )}
    />
  );
}

export interface TrackersShellProps {
  team: TeamSummary;
  project: TeamProject;
  teamSession: TeamSession;
  /**
   * The project's tracker room, owned by `ProjectTrackerSessionProvider` so
   * Docs reads the same one. Null until the org roster resolves.
   */
  session: BrowserTrackerSession | null;
  /** Null until the org roster resolves; the room waits rather than guessing. */
  identity: TrackerIdentity | null;
  navigate: (path: string) => void;
  onSelectProject: (project: TeamProject) => void;
  /** Straight from the URL; `useTrackerSelection` resolves them. */
  trackerType: string | null;
  viewId: string | null;
  activeItemId: string | null;
  /**
   * The open item, or null. Rendered beside the view rather than in place of
   * it, so the route that names an item still shows the rows it came from.
   */
  detail?: ReactNode;
  children: ReactNode;
}

/**
 * Pane, view and -- when an item is open -- the detail beside it.
 *
 * The detail sits at one JSX position, as a sibling of the view region, and
 * never inside it. Which presentation the view is drawing (list, table, board)
 * is state held inside `TrackerViewSurface`, so keeping the detail out of that
 * subtree is what makes a mode switch a repaint rather than a remount: the item
 * body mounts a Y.Doc binding that only paints events observed after it mounts
 * (NIM-1764), and a remount would leave it blank. Desktop's `TrackerDocumentView`
 * holds the same invariant for the same reason.
 *
 * Exported so that invariant can be tested against the real view surface rather
 * than a stand-in; see `test/trackerDetailMount.test.tsx`.
 */
export function TrackersFrame({
  pane,
  header,
  detail,
  children,
  routeKey,
  navigationLabel = 'Trackers',
}: {
  pane: ReactNode;
  header: ReactNode;
  detail: ReactNode;
  children: ReactNode;
  /**
   * Everything in the route the pane can change, as one string. On a phone the
   * pane is an overlay over the list, so choosing a type in it has to put it
   * away — otherwise the list the reader just asked for is behind the thing
   * they asked with. Optional: the phone harness mounts this frame with no
   * route at all.
   */
  routeKey?: string;
  navigationLabel?: ReactNode;
}) {
  const [shell, setShell] = useState<HTMLElement | null>(null);
  const [contentBody, setContentBody] = useState<HTMLElement | null>(null);
  // Read only by the phone block in `styles.css`, where the pane is an overlay
  // over the list rather than a column beside it. Above 560px the pane is a
  // grid column and nothing consults this — which is why the pane below stays
  // mounted either way, so widening back past the breakpoint restores it
  // without this component having to watch the viewport.
  //
  // Seeded closed on a phone: a cold link names an item, and opening onto the
  // type list would cover it.
  const [paneOpen, setPaneOpen] = useState(() => !isPhoneViewport());
  const drawer = useTrackerPhoneDrawer(paneOpen, () => setPaneOpen(false));
  useEffect(() => {
    if (isPhoneViewport()) setPaneOpen(false);
  }, [routeKey]);
  // Clamped against the region the pane and the view share, never the window --
  // the window says nothing about how much room this shell was given.
  const shellWidth = useElementWidth(shell);
  // The detail shares the content region with the view, not the whole shell:
  // dragging the navigation pane wider must narrow the list, not the item.
  const contentWidth = useElementWidth(contentBody);
  const paneResize = usePanelResize({
    storageKey: 'trackers-pane',
    defaultWidth: TRACKERS_PANE_DEFAULT_WIDTH,
    edge: 'inline-end',
    label: 'Resize tracker navigation',
    controlsId: TRACKERS_PANE_ID,
    containerWidth: shellWidth,
    min: TRACKERS_PANE_MIN_WIDTH,
    minComplement: TRACKERS_PANE_MIN_CONTENT_WIDTH,
  });
  const detailResize = usePanelResize({
    storageKey: 'trackers-detail',
    defaultWidth: TRACKERS_DETAIL_DEFAULT_WIDTH,
    edge: 'inline-start',
    label: 'Resize the tracker item',
    controlsId: TRACKERS_DETAIL_ID,
    containerWidth: contentWidth,
    min: TRACKERS_DETAIL_MIN_WIDTH,
    minComplement: TRACKERS_DETAIL_MIN_LIST_WIDTH,
  });
  return (
    <section
      ref={setShell}
      // The two grid/flex templates read their widths from custom properties, so
      // they stay real CSS beside the splitters that write them.
      className="browser-trackers-shell relative grid h-full min-h-0 w-full overflow-hidden bg-nim"
      data-personal-state="unavailable"
      data-detail-open={detail ? 'true' : 'false'}
      data-pane-open={paneOpen ? 'true' : 'false'}
      style={{ '--nim-trackers-pane-width': `${paneResize.width}px` } as CSSProperties}
    >
      <aside ref={drawer.pane} role={drawer.modal ? 'dialog' : undefined} aria-modal={drawer.modal || undefined} className="browser-trackers-pane flex min-w-0 min-h-0 flex-col overflow-hidden border-r border-nim bg-nim-secondary" id={TRACKERS_PANE_ID} aria-label="Tracker navigation">
        <button className="tracker-phone-nav-close" type="button" onClick={() => setPaneOpen(false)}>Close navigation</button>
        {pane}
      </aside>
      <div className="browser-trackers-pane-resizer" {...paneResize.separatorProps} />
      {/* Both are always rendered and only ever displayed by the phone block,
          for the same reason `AppShell`'s navigation scrim is: a JS-measured
          breakpoint would need its own resize observer and would disagree with
          the container query beside it for a frame. */}
      {paneOpen && (
        <button
          type="button"
          className="console-pane-scrim absolute inset-0 z-[55] hidden border-none bg-[color-mix(in_srgb,black_45%,transparent)]"
          aria-label="Close tracker navigation"
          onClick={() => setPaneOpen(false)}
        />
      )}
      <div inert={drawer.modal} className="browser-trackers-content flex min-w-0 min-h-0 flex-col overflow-hidden">
        <div className="browser-trackers-pane-bar hidden shrink-0 items-center border-b border-nim px-2 py-1.5">
          <button
            type="button"
            className="console-pane-toggle grid size-8 cursor-pointer place-items-center rounded-md border-none bg-transparent text-nim-muted hover:bg-nim-hover hover:text-nim"
            aria-label={paneOpen ? 'Close tracker navigation' : 'Open tracker navigation'}
            aria-expanded={paneOpen}
            aria-controls={TRACKERS_PANE_ID}
            onClick={() => setPaneOpen(!paneOpen)}
          >
            <DecorativeIcon icon={paneOpen ? 'left_panel_close' : 'left_panel_open'} size={20} />
          </button>
          <div className="tracker-phone-context">{navigationLabel}</div>
        </div>
        <div className="trackers-context-header">{header}</div>
        <div
          ref={setContentBody}
          // A row, because the open item sits beside the rows it came from
          // rather than over them. With nothing open the view region fills it.
          className="browser-trackers-content-body flex min-w-0 min-h-0 flex-1 flex-row overflow-hidden"
          data-detail-open={detail ? 'true' : 'false'}
          style={{ '--nim-trackers-detail-width': `${detailResize.width}px` } as CSSProperties}
        >
          <div className="browser-trackers-view-region flex min-w-0 min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
          {detail ? (
            <>
              <div className="browser-trackers-detail-resizer" {...detailResize.separatorProps} />
              <aside
                className="browser-trackers-detail-pane flex min-w-0 min-h-0 shrink-0 flex-col overflow-hidden border-l border-nim bg-nim [&>*]:min-h-0 [&>*]:flex-1"
                id={TRACKERS_DETAIL_ID}
                aria-label="Tracker item"
              >
                {detail}
              </aside>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}

/** The browser route surface around the project's shared tracker room. */
export function TrackersShell({
  team,
  project,
  teamSession,
  session,
  identity,
  navigate,
  onSelectProject,
  trackerType,
  viewId,
  activeItemId,
  detail = null,
  children,
}: TrackersShellProps) {
  const projectId = getProjectRouteId(project);

  const context = useMemo<TrackersShellContextValue | null>(() => (session ? {
    team,
    project,
    projectId,
    schema: session.schema,
    identity,
    navigate,
    // Carries the list the item is being opened from, so the rows beside the
    // detail stay the rows the reader clicked in.
    itemPath: (itemId: string) => orgTrackerItemPath(team, projectId, itemId, { trackerType: trackerType ?? 'all', viewId }),
    trackerType,
    viewId,
    activeItemId,
  } : null), [session, team, project, projectId, identity, navigate, trackerType, viewId, activeItemId]);

  if (!session || !context) {
    return (
      <section className="browser-trackers-shell is-pending relative grid h-full min-h-0 w-full overflow-hidden bg-nim" data-personal-state="unavailable">
        <CollabLoadingPanel
          title="Opening trackers"
          detail="Connecting to your team’s live tracker room."
        />
      </section>
    );
  }

  return (
    <TrackersUIProvider
      dataSource={session.dataSource}
      identity={identity}
      capabilities={BROWSER_TRACKER_UI_CAPABILITIES}
    >
      <TrackersShellContext.Provider value={context}>
        <TrackersAccessGate team={team}>
          <TrackerBrowseStateProvider key={`${teamSession.orgId}:${projectId}:${teamSession.memberId}`}>
            <TrackerQuickOpenRegistration />
            <TrackersFrame
            navigationLabel={<TrackersMobileContext />}
            pane={<TrackersPane onSelectProject={onSelectProject} />}
            header={<TrackersBreadcrumb />}
            detail={detail}
            routeKey={`${trackerType ?? ''}|${viewId ?? ''}|${activeItemId ?? ''}`}
          >
              {children}
            </TrackersFrame>
          </TrackerBrowseStateProvider>
        </TrackersAccessGate>
      </TrackersShellContext.Provider>
    </TrackersUIProvider>
  );
}
